<template>
    <v-container :fluid="true">
        <v-row v-if="user">
            <v-col class="mx-auto" cols="8">
                <v-expansion-panels variant="popout">
                    <v-expansion-panel>
                        <v-expansion-panel-title class="text-h6 font-weight-bold">Update Profile
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-form v-model="formData.valid" class="mb-3" enctype="multipart/form-data"
                                    validate-on="blur"
                                    @submit.prevent="updateProfile">
                                <v-container>
                                    <v-row>
                                        <v-col cols="12">
                                            <div class="position-relative d-inline-block">
                                                <UserAvatar
                                                    :image="avatar"
                                                    :name="`${formData['first_name'] ?? ''} ${formData['last_name'] ?? ''}`"
                                                    :size="150"/>
                                                <v-menu location="bottom">
                                                    <template #activator="{ props }">
                                                        <v-btn v-bind="props" :loading="uploadingAvatar"
                                                               :disabled="uploadingAvatar"
                                                               icon="mdi-camera" color="primary" size="small"
                                                               elevation="3" aria-label="Change profile photo"
                                                               class="position-absolute"
                                                               style="right: 2px; bottom: 2px; border: 3px solid rgb(var(--v-theme-surface));"/>
                                                    </template>
                                                    <v-list density="compact">
                                                        <v-list-item prepend-icon="mdi-camera" title="Take photo"
                                                                     @click="$refs.cameraInput.click()"/>
                                                        <v-list-item prepend-icon="mdi-image-multiple"
                                                                     title="Choose from library"
                                                                     @click="$refs.galleryInput.click()"/>
                                                    </v-list>
                                                </v-menu>
                                            </div>
                                            <input ref="cameraInput" type="file"
                                                   accept="image/jpeg,image/png,image/gif,image/webp" capture="user"
                                                   class="d-none" @change="onPhotoSelected"/>
                                            <input ref="galleryInput" type="file"
                                                   accept="image/jpeg,image/png,image/gif,image/webp"
                                                   class="d-none" @change="onPhotoSelected"/>
                                            <p class="font-weight-bold mt-2 me-3">{{ user.email }}</p>
                                            <p v-if="authStore.user?.company?.company_code" class="text-body-2 text-medium-emphasis">
                                                Account Number: <span class="font-weight-bold">{{ authStore.user.company.company_code }}</span>
                                            </p>
                                            <v-combobox v-model="formData['title']" :items="relationshipLabels"
                                                        density="compact" hide-details
                                                        label="Your title / role in the family (e.g. Dad, Mom)"
                                                        style="max-width: 320px" class="mt-3"/>
                                        </v-col>
                                    </v-row>
                                    <v-alert v-if="errors.length" class="mb-3 bg-error" closable
                                             close-label="Close Alert"
                                             density="compact" variant="tonal">
                                        <p v-for="error in errors">{{ error }}</p>
                                    </v-alert>
                                    <v-row>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['first_name']"
                                                          :rules="[vRules.required]" density="compact"
                                                          label="First Name"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['middle_name']" density="compact"
                                                          label="Middle Name"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['last_name']"
                                                          :rules="[vRules.required]" density="compact"
                                                          label="Last Name"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete v-model="formData['timezone']" :items="timezones"
                                                            :rules="[vRules.required]" density="compact"
                                                            item-title="title"
                                                            item-value="value" label="Timezone">
                                            </v-autocomplete>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['contact_phone']" density="compact"
                                                          label="Contact Phone"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['mobile_phone']" density="compact"
                                                          label="Mobile Phone"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_1']" density="compact"
                                                          label="Address 1"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_2']" density="compact"
                                                          label="Address 2"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_district']" density="compact"
                                                          label="District"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_city']" density="compact"
                                                          label="City"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_state']" density="compact"
                                                          label="State"/>
                                        </v-col>

                                        <v-col cols="12" sm="6">
                                            <v-text-field v-model="formData['addr_postalcode']" density="compact"
                                                          label="Postal Code"/>
                                        </v-col>
                                        <v-col cols="12" sm="6">
                                            <v-autocomplete v-model="formData['addr_country']" :items="countries"
                                                            density="compact" label="Country"/>
                                        </v-col>
                                    </v-row>
                                </v-container>

                                <v-card-actions class="px-4 d-flex justify-end">
                                    <v-btn :disabled="!formData.valid" color="primary" size="small" type="submit">Save
                                    </v-btn>
                                </v-card-actions>
                            </v-form>
                        </v-expansion-panel-text>
                    </v-expansion-panel>

                    <v-expansion-panel>
                        <v-expansion-panel-title class="text-h6 font-weight-bold">Change Password
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                                     density="compact" variant="tonal">
                                <p v-for="error in errors">{{ error }}</p>
                            </v-alert>
                            <v-form v-model="passwordFormData.valid"
                                    @submit.prevent="updatePassword">
                                <v-card-text class="text-start">
                                    <v-text-field v-model="passwordFormData.current_password"
                                                  :rules="[vRules.required, vRules.minLength(8)]"
                                                  autocomplete="current-password" label="Current Password"
                                                  type="password"/>
                                    <v-text-field v-model="passwordFormData.password"
                                                  :rules="[vRules.required, vRules.minLength(8)]"
                                                  autocomplete="new-password" label="Password"
                                                  type="password"/>
                                    <v-text-field v-model="passwordFormData.password_confirmation"
                                                  :rules="[vRules.required, vRules.minLength(8), vRules.sameAs(passwordFormData.password, 'password')]"
                                                  autocomplete="new-password" label="Confirm Password"
                                                  type="password"/>

                                </v-card-text>
                                <v-card-actions class="d-flex justify-end">
                                    <v-btn :disabled="!passwordFormData.valid" color="primary" size="small"
                                           type="submit" variant="text">Save
                                    </v-btn>
                                </v-card-actions>
                            </v-form>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import {onMounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import timezones from "@/assets/js/timezones.json";
import countries from "@/assets/js/countries.json";
import {useSnackbarStore} from "@/stores/snackBarStore";
import UserAvatar from "@/components/UserAvatar.vue";
import {RELATIONSHIPS} from "@/composables/relationship";

const authStore = useAuthStore();
const {updateAuthUser} = authStore;
const user = ref();
const avatar = ref();
const uploadingAvatar = ref(false);
const errors = ref([]);
const {showSnackbar} = useSnackbarStore();
// Preset titles for the combobox; it stays free-text so a custom one works too.
// Bound to formData.title, which is now a real server field on the user. The
// parenthetical is stripped ("Me (Owner)" -> "Me") so it reads cleanly as a
// tracker subtitle and matches what the mobile app stores.
const relationshipLabels = RELATIONSHIPS.map((r) => r.label.replace(/\s*\(.*\)/, ''));
const formData = reactive({
    valid: false,
});
const passwordFormData = reactive({
    valid: false,
    password: null,
    current_password: null,
    password_confirmation: null,
})

onMounted(() => getProfile())
// Change the avatar on its own (not with the rest-of-form Save): validate the
// size, then upload immediately so the new photo shows right away. Sends the
// required first_name/last_name/timezone unchanged so nothing else is touched.
const onPhotoSelected = (event) => {
    const file = event.target.files && event.target.files[0];
    // Reset the input so picking the same file again still fires @change.
    event.target.value = '';
    if (!file) return;
    // Only the formats the backend accepts (accept="image/*" alone would let a
    // BMP/SVG through to a confusing 422).
    if (!/^image\/(jpeg|png|gif|webp)$/i.test(file.type)) {
        showSnackbar('Please choose a JPEG, PNG, GIF or WebP image.', 'error');
        return;
    }
    if (file.size > 2 * 1024 * 1024) {
        showSnackbar('Image is larger than 2MB. Please choose a smaller photo.', 'error');
        return;
    }
    // /users/profile requires name + timezone. Take them from the last SAVED
    // profile (user.value), not the live form, so changing the photo never
    // persists unsaved field edits and never 422s on a half-edited required
    // field. Guard when they're missing so the failure is explained, not generic.
    const saved = user.value || {};
    if (!saved.first_name || !saved.last_name || !saved.timezone) {
        showSnackbar('Please save your profile details first, then add a photo.', 'error');
        return;
    }
    uploadingAvatar.value = true;
    // Optimistic local preview while the upload is in flight.
    const previewUrl = URL.createObjectURL(file);
    avatar.value = previewUrl;
    send('post', 'users/profile', {
        image: file,
        first_name: saved.first_name,
        last_name: saved.last_name,
        timezone: saved.timezone,
    }, {
        headers: {'Content-Type': 'multipart/form-data'},
    })
        .then((response) => {
            if (response.data?.success && response.data.data?.avatar) {
                avatar.value = response.data.data.avatar;
                // Refresh the saved baseline too, so a later FAILED upload rolls
                // back to this photo rather than the original.
                user.value = {...user.value, ...response.data.data};
                updateAuthUser({...authStore.user, ...response.data.data});
                showSnackbar('Profile photo updated.');
            } else {
                avatar.value = saved.avatar || null;
                showSnackbar("Couldn't update your photo. Please try again.", 'error');
            }
        })
        .catch(() => {
            // Roll the preview back to the saved avatar on failure.
            avatar.value = saved.avatar || null;
            showSnackbar("Couldn't update your photo. Please try again.", 'error');
        })
        .finally(() => {
            uploadingAvatar.value = false;
            // Safe now: avatar.value is always the server URL or the saved
            // avatar here, never previewUrl.
            URL.revokeObjectURL(previewUrl);
        });
}
const getProfile = () => {
    send('get', `users/profile`)
        .then((response) => {
            if (response.data.success) {
                user.value = response.data.data;
                avatar.value = user.value.avatar;
                Object.keys(response.data.data).map((key) => {
                    if (!['email', 'avatar'].includes(key)) {
                        formData[key] = response.data.data[key];
                    }
                })
            }
        })
        .catch(function (errorResponse) {
            console.log(errorResponse)
            showSnackbar(errorResponse.response?.data?.message ?? 'Something went wrong', 'error');
        })
}
const updateProfile = () => {
    if (formData.valid) {
        errors.value = [];
        send('post', `users/profile`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        })
            .then((response) => {
                showSnackbar(response.data.message);
                if (response.data.success) {
                    // Merge (don't replace) so store-only fields like company
                    // survive a partial profile response.
                    updateAuthUser({...authStore.user, ...response.data.data})
                }
            })
            .catch(function (errorResponse) {
                if (errorResponse.response.data && errorResponse.response.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                showSnackbar('Something went wrong', 'error');
                // Guard: this page has no #heading element, so the unguarded call
                // threw a TypeError in the catch on every validation error.
                document.getElementById('heading')?.scrollIntoView({behavior: 'smooth'});
            })
    }
}
const updatePassword = () => {
    if (passwordFormData.valid) {
        errors.value = [];
        send('put', `users/password`, passwordFormData)
            .then((response) => {
                showSnackbar(response.data.message);
                passwordFormData.password = null;
                passwordFormData.current_password = null;
                passwordFormData.password_confirmation = null;
            })
            .catch(function (errorResponse) {
                if (errorResponse.response.data && errorResponse.response.data.errors) {
                    let validationErrors = errorResponse.response.data.errors;
                    Object.keys(validationErrors).forEach((key) => {
                        errors.value.push(validationErrors[key][0]);
                    });
                } else if (errorResponse.response.data.message) {
                    errors.value.push(errorResponse.response.data.message);
                }
                showSnackbar('Something went wrong', 'error');
                console.log(errorResponse)
            })
    }
}
</script>
